import React, { useCallback, useEffect, useState, useRef } from "react";
import Layout4 from "../../DataLayouts/Layout4";
import LayersIcon from "@mui/icons-material/Layers";
import ViewHeadlineIcon from "@mui/icons-material/ViewHeadline";
import useAuthStore from "../../../Zustand/Store/useAuthStore";
import { MAIN_URL } from "../../../Configurations/Urls";
import toast from "react-hot-toast";
import TableDataGeneric from "../../../Configurations/TableDataGeneric";
import { useNavigate, useSearchParams } from "react-router-dom";
import { operatorRequiresValue } from "../../../Components/filters/operators";

const DEFAULT_PAGINATION = {
  page: 1,
  pageSize: 10,
  total: 0,
  totalPages: 1,
  hasNext: false,
  hasPrev: false,
};

const DEFAULT_URL_PARAMS = {
  page: "1",
  pageSize: "10",
  sortBy: "level_order",
  sortOrder: "asc",
};

const buildFiltersFromParams = (searchParams) => {
  const filters = [];
  const processedFields = new Set();

  searchParams.forEach((value, key) => {
    if (
      key.endsWith("_operator") ||
      key.endsWith("_logic") ||
      !key.startsWith("filter_")
    )
      return;

    const field = key.replace("filter_", "");
    if (processedFields.has(field)) return;
    processedFields.add(field);

    const operator = searchParams.get(`filter_${field}_operator`) || "contains";
    const requiresValue = operatorRequiresValue(operator);

    if (!requiresValue || value) {
      let filterValue = requiresValue ? value : null;

      if (requiresValue && filterValue) {
        if (["equals", "not_equals"].includes(operator)) {
          filterValue = filterValue
            .split(",")
            .map((v) => v.trim().toLowerCase());
        } else if (["between", "not_between"].includes(operator)) {
          filterValue = filterValue.split(",").map((v) => v.trim());
        }
      }

      filters.push({ field, operator, value: filterValue });
    }
  });

  const search = searchParams.get("search");
  if (search) {
    filters.push({
      field: "level_name",
      operator: "contains",
      value: search,
    });
  }

  return filters;
};

function OrganizationLevelList() {
  const [levels, setLevels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [paginationData, setPaginationData] = useState(DEFAULT_PAGINATION);
  const { userData } = useAuthStore();
  const org = userData?.organization;
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const fetchControllerRef = useRef(null);
  const hasInitializedURL = useRef(false);

  useEffect(() => {
    if (hasInitializedURL.current) return;

    const params = new URLSearchParams(searchParams);
    let needsUpdate = false;

    Object.entries(DEFAULT_URL_PARAMS).forEach(([key, defaultValue]) => {
      if (!params.has(key)) {
        params.set(key, defaultValue);
        needsUpdate = true;
      }
    });

    if (needsUpdate) {
      setSearchParams(params, { replace: true });
    }

    hasInitializedURL.current = true;
  }, [searchParams, setSearchParams]);

  useEffect(() => {
    if (!org?.organization_id || !hasInitializedURL.current) return;

    fetchControllerRef.current?.abort();
    fetchControllerRef.current = new AbortController();

    setLoading(true);

    const fetchData = async () => {
      try {
        const filters = buildFiltersFromParams(searchParams);
        const requestBody = {
          filters,
          page: Number.parseInt(searchParams.get("page"), 10) || 1,
          pageSize: Number.parseInt(searchParams.get("pageSize"), 10) || 10,
          sort: {
            field: searchParams.get("sortBy") || "level_order",
            direction: searchParams.get("sortOrder") || "asc",
          },
        };

        const response = await fetch(
          `${MAIN_URL}/api/organizations/${org.organization_id}/organization-levels-filtered`,
          {
            method: "POST",
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
              "Content-Type": "application/json",
            },
            body: JSON.stringify(requestBody),
            signal: fetchControllerRef.current.signal,
          }
        );

        let data = [];
        if (response.ok) {
          const result = await response.json();
          if (result.success && result.data) {
            data = result.data;

            if (result.pagination) {
              const pageSize =
                result.pagination.pageSize || result.pagination.limit || 10;
              setPaginationData({
                page:
                  result.pagination.page ||
                  Number.parseInt(searchParams.get("page"), 10) ||
                  1,
                pageSize,
                total: result.pagination.total || 0,
                totalPages: result.pagination.totalPages || 1,
                hasNext: result.pagination.hasNext || false,
                hasPrev: result.pagination.hasPrev || false,
              });
            }
          } else {
            setPaginationData(DEFAULT_PAGINATION);
          }
        } else {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const rows = data.map((item) => ({
          ...item,
          id: item.organization_level_id,
          is_active_label: item.is_active ? "Active" : "Inactive",
          notice_period_days_display:
            item.notice_period_days != null ? `${item.notice_period_days} days` : "-",
          description: item.description || "-",
        }));

        setLevels(rows);
      } catch (error) {
        if (error.name === "AbortError") {
          return;
        }
        console.error("Error fetching organization levels:", error);
        toast.error("Failed to load organization levels");
        setLevels([]);
      } finally {
        setLoading(false);
      }
    };

    fetchData();

    return () => {
      fetchControllerRef.current?.abort();
    };
  }, [org?.organization_id, searchParams]);

  const handleDelete = useCallback(
    async (id) => {
      try {
        const response = await fetch(
          `${MAIN_URL}/api/organizations/${org.organization_id}/organization-level/${id}`,
          {
            method: "DELETE",
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
          }
        );

        if (response.ok) {
          const result = await response.json();
          toast.success(result?.message || "Organization level deleted successfully");
          const params = new URLSearchParams(searchParams);
          setSearchParams(params);
        } else {
          const errorData = await response.json();
          if (response.status === 401) {
            toast.error("Session Expired!");
            window.location.href = "/login";
          } else {
            const errorMessage =
              errorData.message ||
              errorData.errors?.[Object.keys(errorData.errors || {})[0]]?.[0] ||
              "Failed to delete organization level";
            toast.error(errorMessage);
          }
        }
      } catch (error) {
        console.error("Delete failed:", error);
        toast.error("Failed to delete organization level");
      }
    },
    [org?.organization_id, searchParams, setSearchParams]
  );

  const handleEdit = useCallback(
    (item) => {
      navigate(
        `/organization-configration/organization-level/edit/${item.id || item.organization_level_id}`
      );
    },
    [navigate]
  );

  const handleShow = useCallback(
    (item) => {
      navigate(
        `/organization-configration/organization-level/view/${item.id || item.organization_level_id}`
      );
    },
    [navigate]
  );

  return (
    <>
      <Layout4
        loading={loading}
        heading={"Organization Levels"}
        btnName={"Add Level"}
        Data={levels}
        tableHeaders={[
          { name: "Level Name", value_key: "level_name", textStyle: "capitalize" },
          { name: "Level Code", value_key: "level_code", textStyle: "capitalize" },
          { name: "Order", value_key: "level_order" },
          { name: "Notice Period", value_key: "notice_period_days_display" },
          { name: "Status", value_key: "is_active_label" },
        ]}
        Icons={[
          <LayersIcon sx={{ fontSize: 60, color: "grey.500", mb: 2 }} />,
          <ViewHeadlineIcon color="primary" />,
          <LayersIcon sx={{ color: "text.secondary" }} />,
        ]}
        messages={[
          "organization levels",
          "Organization Levels",
          "Add Level",
          "organization level",
        ]}
        Route={"/organization-configration/organization-level"}
        setData={setLevels}
        DeleteFunc={handleDelete}
      />

      <TableDataGeneric
        tableName="organization_levels"
        primaryKey="organization_level_id"
        heading="Organization Levels"
        data={levels}
        sortname="level_order"
        showActions={true}
        Route="/organization-configration/organization-level"
        DeleteFunc={handleDelete}
        EditFunc={handleEdit}
        handleShow={handleShow}
        token={localStorage.getItem("token")}
        edit_delete_action={[""]}
        organizationUserId={userData?.organization_user_id}
        showLayoutButtons={true}
        paginationData={paginationData}
        filterModule="organization_levels"
        config={{
          defaultVisibleColumns: [
            "level_name",
            "level_code",
            "level_order",
            "notice_period_days_display",
            "is_active_label",
            "description",
          ],
          mandatoryColumns: ["level_name", "level_order"],
        }}
      />
    </>
  );
}

export default OrganizationLevelList;
